import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService } from '../api/services/project.service';
import type { StartupDto } from '../api/services/project.service';

/** The single cache identity the user's startups list lives under. */
export const startupsQueryKey = ['startups'] as const;

/**
 * The user's startups list.
 *
 * Replaces the scattered `dispatch(getUserStartups())` +
 * `useAppSelector(selectUserStartups)` pairs (StartupListPage, the startup
 * shell, the scope page, the profile hook, onboarding, and the startup-page
 * facade). Every caller now shares this one query key, so the ~6 independent
 * fetches — each previously guarded with an ad-hoc `if (!startups.length)` —
 * collapse into a single deduped, cached request.
 */
export function useStartups() {
  return useQuery<StartupDto[]>({
    queryKey: startupsQueryKey,
    queryFn: () => projectService.getUserStartups(),
    // The list is stable within a session and mutations already call
    // `useInvalidateStartups()` on create/rename/delete, so freshness is
    // event-driven rather than time-driven. A long stale window turns every
    // route change into a cache hit (no more `projects?limit=100` on each
    // navigation), and we don't need a window-focus refetch for a list that
    // only changes via our own mutations. A hard page refresh still refetches
    // (React-Query's in-memory cache is gone) — that request is inherent.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Invalidate the startups cache so the next read refetches — call after a
 * create / rename / delete. Replaces the old "await dispatch(getUserStartups())
 * to refresh" pattern.
 */
export function useInvalidateStartups() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: startupsQueryKey }),
    [queryClient],
  );
}
